import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { createRoot } from 'react-dom/client';
import InfoWindowContent from '../InfoWindow/InfoWindowContent/InfoWindowContent';
import isEmpty from 'lodash/isEmpty';

const PolygonEllipse = ({
  ellipse,
  maps,
  map,
  setValues,
  handlerInstance,
  ellipseInstance,
  polygonRef,
  infoWindow,
  infoWindowData,
  layers,
  switchedLayer,
  onPolygonClick,
  polygonData,
  mapInForm,
}) => {
  const ref = useRef({
    initialLatLng: null,
    rotation: 0,
    lastRotation: 0,
  });

  const onDragStart = e => {
    ref.current.initialLatLng = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
      path: ellipseInstance
        .getPath()
        .getArray()
        .map(item => item.toJSON()),
    };
  };

  const onDrag = e => {
    const { initialLatLng } = ref.current;
    const angle = ellipseInstance.distanceAngle(
      {
        lat: initialLatLng.lat,
        lng: initialLatLng.lng,
      },
      { lat: e.latLng.lat(), lng: e.latLng.lng() },
    );

    ellipseInstance.setPaths([initialLatLng.path]);

    const center = ellipseInstance.getCenter();
    ellipseInstance.rotate(angle, center, map);

    ref.current.rotation = angle;

    handlerInstance.setHandlerPosition({
      angle: ref.current.lastRotation
        ? angle + ref.current.lastRotation
        : angle,
      radiusX: ellipse.radiusX,
      radiusY: ellipse.radiusY,
      point: center,
    });
  };

  const onDragEnd = () => {
    setValues([
      {
        ...ellipse,
        center: {
          ...ellipse.center,
          ...ellipseInstance.getCenter().toJSON(),
        },
        radiusX: ellipse.radiusX,
        radiusY: ellipse.radiusY,
        rotationAngle: ref.current.lastRotation + ref.current.rotation,
      },
    ]);

    ref.current.lastRotation += ref.current.rotation;
  };

  useEffect(() => {
    ellipseInstance.setMap(map);

    const dragStart = maps.event.addListener(
      ellipseInstance,
      'dragstart',
      onDragStart,
    );

    const drag = maps.event.addListener(ellipseInstance, 'drag', onDrag);
    const dragEnd = maps.event.addListener(
      ellipseInstance,
      'dragend',
      onDragEnd,
    );

    if (!infoWindow) {
      return undefined;
    }

    const content = document.createElement('div');
    const root = createRoot(content);
    root.render(
      React.createElement(
        InfoWindowContent,
        { position: infoWindowData },
        null,
      ),
    );

    infoWindow.setContent(content);

    if (polygonRef.current?.[polygonData.layer]) {
      polygonRef.current[polygonData.layer].push(ellipseInstance);
    } else {
      Object.assign(polygonRef.current, {
        [polygonData.layer]: [],
      });
      polygonRef.current[polygonData.layer].push(ellipseInstance);
    }

    const ellipseClick = maps.event.addListener(ellipseInstance, 'click', e => {
      // close all other info windows
      polygonRef.current.polygonsInfoWindow.forEach(item => item.close());
      // set the position of the infowindow
      infoWindow.setPosition(e.latLng);
      // open it
      infoWindow.open(map);
    });

    // close the infowindow
    maps.event.addListener(infoWindow, 'domready', () => {
      const someButton = document.getElementById(
        `btn-click-${infoWindowData.pageName}`,
      );
      if (someButton) {
        someButton.addEventListener('click', e => {
          //close the infowindow
          infoWindow.setMap(null);
          infoWindow.close();
          if (typeof onPolygonClick === 'function') {
            onPolygonClick({
              event: e,
              id: infoWindowData.id,
              pageName: infoWindowData.pageName,
            });
          }
          maps.event.trigger(map, 'click', e);
        });
      }
    });
    // keep track of info windows
    if (polygonRef.current?.polygons) {
      polygonRef.current.polygonsInfoWindow.push(infoWindow);
    } else {
      Object.assign(polygonRef.current, {
        polygonsInfoWindow: [],
      });
      polygonRef.current.polygonsInfoWindow.push(infoWindow);
    }

    return () => {
      maps.event.removeListener(dragStart);
      maps.event.removeListener(drag);
      maps.event.removeListener(dragEnd);
      infoWindow.setMap(null);
      maps.event.removeListener(ellipseClick);
      ellipseInstance.setMap(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onDrag]);

  // set bounds for ellipse if it not opened in the form component
  useEffect(() => {
    if (ellipse.strokeOpacity === 1 && !mapInForm) {
      // ellipse is highlighted
      const bounds = new maps.LatLngBounds();
      const ellipsePaths = ellipseInstance.getPath();
      for (let i = 0; i < ellipsePaths.length; i += 1) {
        const lat = ellipsePaths.getAt(i).lat();
        const lng = ellipsePaths.getAt(i).lng();
        bounds.extend(new maps.LatLng(lat, lng));
      }
      map.fitBounds(bounds);
    }
  }, [ellipseInstance]);

  // remove the polygon layers
  useEffect(() => {
    if (
      !isEmpty(polygonRef?.current) &&
      Object.keys(layers).some(k => !layers[k])
    ) {
      switchedLayer.forEach(f => {
        if (!polygonRef.current[f]) {
          polygonRef.current[f] = [];
        }
        const copy = [...polygonRef.current[f]];
        copy.forEach((poly, i) => {
          if (f === poly.layer) {
            poly.setVisible(false);
            polygonRef.current[f].splice(i, 1);
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, map, ellipse, polygonRef]);
  return null;
};
PolygonEllipse.propTypes = {
  ellipse: PropTypes.object,
  maps: PropTypes.object,
  map: PropTypes.object,
  polygonData: PropTypes.object,
  setValues: PropTypes.func,
  handlerInstance: PropTypes.object,
  ellipseInstance: PropTypes.object,
  infoWindow: PropTypes.object,
  infoWindowData: PropTypes.object,
  polygonRef: PropTypes.object,
  mapInForm: PropTypes.bool,
  layers: PropTypes.object,
  switchedLayer: PropTypes.array,
  onPolygonClick: PropTypes.func,
};
export default PolygonEllipse;
